import React, { useState } from "react";
import { Building2, User, Mail, Phone, Loader2, CheckCircle, FileText } from "lucide-react";
import { formatINR } from "../utils/pricingEngine";

const API_BASE = "https://api.oratorhype.com";
// const API_BASE = "http://localhost:3000";
/**
 * InquiryForm - Simplified version with only organisation details
 * Collects org details, submits quote to backend, triggers QuotationPreview.
 */
const InquiryForm = ({ cartItems = [], subtotal = 0, gstAmount = 0, totalWithGST = 0, onQuoteReady }) => {
  const [formData, setFormData] = useState({
    orgName: "",
    contactPerson: "",
    email: "",
    phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      setError("Please add at least one product to the cart before submitting.");
      return;
    }
    setError("");
    setLoading(true);

    // Build the items for the backend
    const quoteItems = cartItems.map((item) => ({
      name: item.name,
      brand: item.brand,
      model: item.model,
      quantity: item.quantity,
      price: item.amazonPrice,
      deliveryType: item.deliveryType,
      weight: item.weight,
    }));

    const payload = {
      name: formData.contactPerson,
      email: formData.email,
      company: formData.orgName,
      phone: formData.phone,
      projectScope: "Hardware procurement",
      budget: "Custom",
      timeline: "Standard (7-10 days)",
      cartItems: quoteItems,
    };

    try {
      const res = await fetch(`${API_BASE}/api/hardware/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!result.success) {
        setError(result.error || "Failed to submit quotation.");
        return;
      }
      setSuccess(true);

      // Pass finalized quote data up for preview rendering
      onQuoteReady?.({
        ...formData,
        cartItems,
        subtotal,
        gstAmount,
        totalWithGST,
        quoteNumber: `OHPL/${new Date().getFullYear().toString().slice(-2)}-${String(new Date().getMonth() + 1).padStart(2, "0")}/${String(Math.floor(Math.random() * 900) + 100).padStart(3, "0")}`,
        date: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }),
      });
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-5">
            <CheckCircle className="h-10 w-10 text-emerald-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Quotation Submitted!</h3>
          <p className="text-gray-500">
            Your quote has been sent to <strong>{formData.email}</strong>. Our team will reach out
            within 24 hours. Your quotation is generated below.
          </p>
        </div>
      </section>
    );
  }

  const inputClass =
    "w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm placeholder-gray-400";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  return (
    <section id="inquiry-form" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Generate Your Quotation</h2>
          <p className="text-gray-500">
            Enter your organisation details to generate a precise quotation.
          </p>
        </div>

        {/* Cart summary strip - NO GST */}
        {cartItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-8 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
              <FileText className="h-4 w-4" />
              {cartItems.length} item{cartItems.length > 1 ? "s" : ""} in cart
            </div>
            <div className="text-sm text-blue-700">
              Unit Price (Excl. GST):{" "}
              <strong className="text-blue-900 text-base">₹{formatINR(subtotal)}</strong>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organisation Details Only */}
          <div className="bg-gray-50 rounded-2xl p-6 space-y-5">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-blue-500" />
              Organisation Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Organisation Name *</label>
                <input 
                  name="orgName" 
                  value={formData.orgName} 
                  onChange={handleChange} 
                  required 
                  placeholder="Your Company Pvt Ltd" 
                  className={inputClass} 
                />
              </div>
              <div>
                <label className={labelClass}>Contact Person *</label>
                <input 
                  name="contactPerson" 
                  value={formData.contactPerson} 
                  onChange={handleChange} 
                  required 
                  placeholder="Full Name" 
                  className={inputClass} 
                />
              </div>
              <div>
                <label className={labelClass}>Email Address *</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  placeholder="email@company.com" 
                  className={inputClass} 
                />
              </div>
              <div>
                <label className={labelClass}>Phone Number *</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                  placeholder="+91 98765 43210" 
                  className={inputClass} 
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || cartItems.length === 0}
            className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition text-base"
          >
            {loading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Generating Quotation…</>
            ) : (
              <><FileText className="h-5 w-5" /> Get Quote</>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default InquiryForm;